import type { CampaignDraftPostItem } from '@/entities/campaign';
import { importPostsFromCsv } from '@/entities/campaign';
import { exportToCSV } from '@/shared/utils/export-to-csv';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback } from 'react';
import { toast } from 'sonner';

import {
  type PostCsvPreviewState,
  buildCampaignNameSuggestion,
  getUnsupportedPlatformCount,
  warnUnsupportedCampaignPlatforms,
  withFallbackPostRow,
} from '../../../../hooks/campaign-create-shared';

type UseCampaignPostCsvImportArgs = {
  campaignItemSlotLimit: number;
  draftPostItems: CampaignDraftPostItem[];
  remainingSlots: number;
  setPostItems: Dispatch<SetStateAction<CampaignDraftPostItem[]>>;
};

export function useCampaignPostCsvImport({
  campaignItemSlotLimit,
  draftPostItems,
  remainingSlots,
  setPostItems,
}: UseCampaignPostCsvImportArgs) {
  const downloadPostTemplate = useCallback(() => {
    exportToCSV(
      [
        'post_url',
        // PLATFORM_EXPANSION_MARK: Add additional platform examples when campaign tracking expands beyond TikTok.
        'https://www.tiktok.com/@syncly/video/123456789',
      ].join('\n'),
      'campaign-post-template.csv',
    );
  }, []);

  const previewPostCsvImport = useCallback(
    async (file: File) => {
      const result = importPostsFromCsv({
        text: await file.text(),
        fileName: file.name,
        existingRows: draftPostItems,
        availableSlots: remainingSlots,
        itemLimit: campaignItemSlotLimit,
      });

      const limitSkippedCount = result.summary.errors.filter((error) =>
        error.message.includes('campaign limit'),
      ).length;
      const unsupportedPlatformCount = getUnsupportedPlatformCount(result.summary);

      return {
        rows: result.rows,
        summary: result.summary,
        suggestedCampaignName: buildCampaignNameSuggestion(file.name),
        limitSkippedCount,
        unsupportedPlatformCount,
        validationErrorCount:
          result.summary.errors.length - limitSkippedCount - unsupportedPlatformCount,
      };
    },
    [campaignItemSlotLimit, draftPostItems, remainingSlots],
  );

  const applyPostCsvImport = useCallback(
    (preview: PostCsvPreviewState) => {
      const unsupportedPlatformWarning = warnUnsupportedCampaignPlatforms(
        preview.unsupportedPlatformCount,
      );
      if (preview.summary.added === 0) return;
      setPostItems(withFallbackPostRow(preview.rows));
      if (unsupportedPlatformWarning) {
        toast.warning(unsupportedPlatformWarning);
      }
    },
    [setPostItems],
  );

  return {
    applyPostCsvImport,
    downloadPostTemplate,
    previewPostCsvImport,
  };
}
